import React, {
  useCallback,
  Suspense,
  useRef,
  useState,
  useEffect,
} from 'react'
import {
  CustomAuth,
  ScreenLayout,
  SecureStore,
  countryCodes,
} from '@libs/utils'
import { useQuery } from '@tanstack/react-query'
import { Text } from 'react-native'
import { useAtom } from 'jotai'
import { useUpdateProfile } from '@app-hero/shared-hooks'
import { ProfilePlaceHolder } from '@apphero/assets'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { getUserDetails, getCountryList } from '../../api'
import DesktopView from './DesktopView'
import { userProfile } from '../../utils/atom'
import MobileView from './MobileView'

const Profile = () => {
  const [userprofile, setUserDetails] = useAtom(userProfile)
  const [profileDetails, setProfileDetails] = useState({
    fullName: '',
    firstName: '',
    lastName: '',
    mobile: {
      dialCode: '',
      mobileNumber: '',
      mobileCountryCode: '',
      mobileCountry: '',
    },
    country: '',
    countryCode: '',
  })
  const [dropdownTop, setDropdownTop] = useState(0)
  const [dropdownRight, setDropdownRight] = useState(0)
  const [dropdownWidth, setDropDownWidth] = useState(0)
  const [isLastNameAvailable, setIsLastNameAvailable] = useState()
  const [ctaEnable, setCTAEnable] = useState(false)
  const [countryDropdownData, setCountryDropDownData] = useState({
    countryData: [],
    selectedCountry: {},
  })
  const [isLoading, setIsLoading] = useState()
  const [isProfileUpdating, setIsProfileUpdating] = useState()
  const [successPopup, setSuccessPopup] = useState({
    visible: false,
    message: '',
  })
  const navigation = useNavigation()
  const isFocused = useIsFocused()
  const [mobileNumberError, setMobileNumberError] = useState('')
  const [firstNameError, setFirstNameError] = useState('')
  const [lastNameError, setLastNameError] = useState('')
  const mobileMaxLength = Math.max(
    0,
    15 - (profileDetails.mobile.dialCode || '').length,
  )

  const DropdownButton = useRef()

  const toggleDropdown = (visible, ref) => {
    if (visible) {
      return
    }
    if (!visible) {
      ref?.current?.measure((_fx, _fy, _w, h, _px, py) => {
        setDropdownTop(py + 50)
        setDropdownRight(_px - 10)
        setDropDownWidth(_w)
      })
    }
  }

  const handleLogout = async () => {
    setIsLoading(true)
    await CustomAuth.logOut()
    await SecureStore.deleteItemAsync('userProfile')
    await SecureStore.deleteItemAsync('config')
    await SecureStore.deleteItemAsync('selectedProgramId')
    await SecureStore.deleteItemAsync('userLanguage')
    await SecureStore.deleteItemAsync('filters')

    navigation.navigate('login')
    setIsLoading(false)
  }

  function isValidName(name) {
    const specialCharPattern = /[^a-zA-Z ]/

    return !specialCharPattern.test(name)
  }

  const handleChange = (field, selectedValue) => {
    if (field === 'firstName') {
      if (!isValidName(selectedValue)) {
        setFirstNameError('Please enter a valid First Name')
        setCTAEnable(false)
        return setProfileDetails((previousData) => ({
          ...previousData,
          [field]: selectedValue,
        }))
      }
      setFirstNameError('')
      setCTAEnable(true)
      return setProfileDetails((previousData) => ({
        ...previousData,
        [field]: selectedValue,
      }))
    }
    if (field === 'lastName') {
      if (!isValidName(selectedValue)) {
        setLastNameError('Please enter a valid Last Name')
        setCTAEnable(false)
        return setProfileDetails((previousData) => ({
          ...previousData,
          [field]: selectedValue,
        }))
      }
      setLastNameError('')
      setCTAEnable(true)
      return setProfileDetails((previousData) => ({
        ...previousData,
        [field]: selectedValue,
      }))
    }
    if (field === 'profilePictureLocation') {
      setCTAEnable(true)
      setUserDetails((prev) => ({
        ...prev,
        selectedImage: selectedValue.selectedFile,
      }))
      return setProfileDetails((previousData) => ({
        ...previousData,
        base64Picture: selectedValue.file,
        fileName: selectedValue.fileName,
        selectedImage: selectedValue.selectedFile,
      }))
    }
    if (field === 'mobile') {
      const { dialCode, mobileNumber } = selectedValue
      const fullNumber = `${dialCode}${mobileNumber}`

      const isValidMobileNumber = isValidPhoneNumber(fullNumber)

      if (!isValidMobileNumber && selectedValue) {
        setMobileNumberError('Please enter a valid Phone number')
        setCTAEnable(false)
      } else {
        setMobileNumberError('')
        setCTAEnable(true)
      }

      return setProfileDetails((previousData) => ({
        ...previousData,
        [field]: selectedValue,
      }))
    }
    if (field === 'country') {
      setCTAEnable(true)

      return setProfileDetails((previousData) => ({
        ...previousData,
        [field]: selectedValue.Label,
        countryCode: selectedValue.Value,
      }))
    }
    setCTAEnable(true)
    return setProfileDetails((previousData) => ({
      ...previousData,
      [field]: selectedValue,
    }))
  }

  const fetchUserDetails = async () => {
    const response = await getUserDetails({ email: userprofile.email })
    return response || {}
  }

  // fetch userProfile
  const { data: userDetails } = useQuery({
    queryKey: ['getUserProfile'],
    queryFn: fetchUserDetails,
    initialData: [],
    enabled: !!userprofile.email,
  })

  const { mutation } = useUpdateProfile()

  const handleSave = async () => {
    setIsProfileUpdating(true)
    setCTAEnable(false)
    await mutation.mutateAsync({
      updatedProfile: profileDetails,
      isLastNameAvailable,
    })

    setIsProfileUpdating(false)
  }

  useEffect(() => {
    if (!isFocused) return

    if (Object.keys(userDetails).length > 0) {
      const newUserDetails = {
        email: userprofile?.email,
        userID: userprofile.userID,
        accessToken: userprofile?.accessToken,
        bucketId: userprofile?.bucketId,
        ...userDetails,
      }
      setUserDetails(newUserDetails)
    }
  }, [userDetails, isFocused])

  useEffect(() => {
    if (!isFocused) return
    if (Object.keys(userprofile).length > 0) {
      let selectedCountry = {}
      let newUserDetails = { ...userprofile }

      const countryData = Object.entries(countryCodes)?.map(([, item]) => {
        if (item?.dial_code === userprofile?.phoneNumber?.split('-')[0]) {
          selectedCountry = {
            countryNames: item.name,
            dial_code: item?.dial_code,
          }
        }
        return { countryNames: item.name, dial_code: item?.dial_code }
      })
      const firstNameValue = userprofile?.firstName || ''
      const lastNameValue = userprofile?.lastName || ''
      const firstName =
        firstNameValue.charAt(0).toUpperCase() + firstNameValue.slice(1)
      const lastName = lastNameValue
        ? lastNameValue.charAt(0).toUpperCase() + lastNameValue.slice(1)
        : ''
      newUserDetails = {
        ...newUserDetails,
        fullName: `${firstName} ${lastName}`,
        mobile: {
          dialCode: userprofile?.phoneNumber?.split('-')[0],
          mobileNumber: userprofile?.phoneNumber?.split('-')[1],
        },
      }
      delete newUserDetails.phoneNumber
      setCountryDropDownData({ countryData, selectedCountry })
      setProfileDetails(newUserDetails)
    }
  }, [isFocused, userprofile])

  const getImageSource = () => {
    if (profileDetails?.selectedImage) {
      return profileDetails.selectedImage
    }
    if (profileDetails?.profilePictureLocation) {
      return { uri: profileDetails?.profilePictureLocation }
    }
    return ProfilePlaceHolder
  }

  const { data: countryList } = useQuery({
    queryKey: ['getCountryList'],
    queryFn: getCountryList,
    enabled: isFocused,
  })

  const LayoutView = useCallback(
    ScreenLayout.withLayoutView(DesktopView, MobileView, MobileView),
    [],
  )

  const hasError = () =>
    Boolean(lastNameError || firstNameError || mobileNumberError) || !ctaEnable

  useEffect(() => {
    setCTAEnable(!hasError())
  }, [
    lastNameError,
    firstNameError,
    mobileNumberError,
    profileDetails,
    ctaEnable,
  ])

  const viewProps = {
    countryDropdownData,
    ctaEnable,
    countryList,
    DropdownButton,
    dropdownRight,
    dropdownTop,
    dropdownWidth,
    mobileMaxLength,

    isLoading,
    isProfileUpdating,
    isLastNameAvailable,
    profileDetails,
    successPopup,
    getImageSource,
    handleChange,
    handleLogout,
    handleSave,
    setProfileDetails,
    setIsLastNameAvailable,
    setUserDetails,
    setSuccessPopup,
    setCTAEnable,
    toggleDropdown,
    ProfilePlaceHolder,
    navigation,
    mobileNumberError,
    firstNameError,
    lastNameError,
  }

  return (
    <Suspense fallback={<Text>Loading</Text>}>
      <LayoutView {...viewProps} />
    </Suspense>
  )
}

export default Profile
